import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, Tag, Rss, ChevronLeft, ChevronRight, Zap, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import Header from '../Header';
import Footer from '../Footer';

const TAGS_VISIBLE = 15;

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Tag pill ──────────────────────────────────────────────────────────────────
function TagPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border
        ${active
          ? 'bg-primary/20 border-primary/50 text-primary glow-purple'
          : 'bg-card/40 border-border/40 text-muted-foreground hover:border-primary/30 hover:text-foreground'
        }`}
    >
      <Tag className="w-3 h-3" />
      {label}
    </button>
  );
}

// ── Post card ─────────────────────────────────────────────────────────────────
function PostCard({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group glass rounded-2xl overflow-hidden border border-border/30 hover:border-primary/40
        transition-all duration-300 hover-lift hover:shadow-[0_0_30px_hsl(280_85%_58%/0.15)]
        flex flex-col ${featured ? 'md:flex-row' : ''}`}
    >
      {/* Cover image or gradient placeholder */}
      <div
        className={`relative overflow-hidden bg-gradient-to-br from-primary/20 via-card to-secondary/10
          flex items-center justify-center flex-shrink-0
          ${featured ? 'md:w-2/5 h-48 md:h-auto' : 'h-40'}`}
      >
        {post.cover_image ? (
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 opacity-30">
            <Rss className="w-10 h-10 text-primary" />
            <span className="text-xs text-primary font-mono tracking-widest uppercase">Auth0.Expert</span>
          </div>
        )}
        {/* Tag strip */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {post.tags?.slice(0, 2).map(tag => (
            <span key={tag} className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-background/80 backdrop-blur text-primary border border-primary/30">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-6 gap-3">
        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">{post.author?.name}</span>
          <span>·</span>
          <span>{new Date(post.published_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          {post.reading_time_minutes && (
            <>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {post.reading_time_minutes} min
              </span>
            </>
          )}
        </div>

        {/* Title */}
        <h2 className={`font-bold text-foreground leading-snug group-hover:text-primary transition-colors duration-200
          ${featured ? 'text-xl md:text-2xl' : 'text-lg'}`}>
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
          {post.excerpt}
        </p>

        {/* Footer: rating + read link */}
        <div className="flex items-center justify-between mt-1">
          {post.rating_count > 0 ? (
            <div className="flex items-center gap-1 text-xs text-amber-400">
              <Star className="w-3.5 h-3.5 fill-amber-400" />
              <span className="font-semibold">{Number(post.rating_avg).toFixed(1)}</span>
              <span className="text-muted-foreground">({post.rating_count})</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground/50">Pas encore noté</span>
          )}
          <div className="flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all duration-200">
            Lire
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Main BlogIndex ────────────────────────────────────────────────────────────
export default function BlogIndex({ onOpenWizard }) {
  const [posts, setPosts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllTags, setShowAllTags] = useState(false);

  // Fetch tags
  useEffect(() => {
    fetch(`${API_BASE}/api/blog/tags`)
      .then(r => r.json())
      .then(setAllTags)
      .catch(() => setAllTags([]));
  }, []);

  // Fetch posts
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 6 });
    if (activeTag) params.append('tag', activeTag);

    fetch(`${API_BASE}/api/blog/posts?${params}`)
      .then(r => {
        if (!r.ok) throw new Error('Erreur réseau');
        return r.json();
      })
      .then(data => {
        setPosts(data.posts || []);
        setTotalPages(data.total_pages || 1);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [activeTag, page]);

  const handleTagClick = (tag) => {
    setActiveTag(prev => (prev === tag ? null : tag));
    setPage(1);
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <Helmet>
        <title>Blog Auth0 Migration — Tutoriels, guides et retours d'expérience | Auth0.Expert</title>
        <meta
          name="description"
          content="Guides techniques Auth0, tutoriels migration IAM, comparatifs et retours d'expérience rédigés par des experts en migration d'identité."
        />
        <link rel="canonical" href="https://auth0.expert/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://auth0.expert/blog" />
        <meta property="og:title" content="Blog Auth0 Migration — Tutoriels & guides | Auth0.Expert" />
        <meta property="og:description" content="Guides techniques Auth0, tutoriels migration IAM, comparatifs et retours d'expérience rédigés par des experts en migration d'identité." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-background grid-bg">
        <Header scrolled onCTAClick={onOpenWizard} />

        {/* Decorative orbs */}
        <div className="pointer-events-none fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-primary/6 blur-3xl" />
          <div className="absolute top-40 right-1/4 w-80 h-80 rounded-full bg-secondary/6 blur-3xl" />
        </div>

        <main className="max-w-6xl mx-auto px-4 pt-28 pb-20">
          {/* Hero header */}
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-bold tracking-widest uppercase bg-primary/10 border border-primary/25 text-primary">
              Blog Technique
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Tout sur la{' '}
              <span className="text-gradient-purple">migration Auth0</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
              Guides techniques, retours d'expérience et stratégies IAM écrits par des experts qui ont migré des millions d'utilisateurs.
            </p>
          </div>

          {/* Tag filters */}
          {allTags.length > 0 && (
            <div className="mb-10">
              <div className="flex flex-wrap gap-2 justify-center">
                <TagPill
                  label="Tous les articles"
                  active={!activeTag}
                  onClick={() => handleTagClick(null)}
                />
                {(showAllTags ? allTags : allTags.slice(0, TAGS_VISIBLE)).map(tag => (
                  <TagPill
                    key={tag}
                    label={tag}
                    active={activeTag === tag}
                    onClick={() => handleTagClick(tag)}
                  />
                ))}
              </div>

              {/* Toggle affichage tags */}
              {allTags.length > TAGS_VISIBLE && (
                <div className="flex justify-center mt-3">
                  <button
                    onClick={() => setShowAllTags(v => !v)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-3 py-1.5 rounded-full border border-border/40 hover:border-primary/30"
                  >
                    {showAllTags ? (
                      <><ChevronUp className="w-3.5 h-3.5" /> Réduire les tags</>
                    ) : (
                      <><ChevronDown className="w-3.5 h-3.5" /> Voir les {allTags.length - TAGS_VISIBLE} autres tags</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
              <p className="text-muted-foreground text-sm">Chargement des articles...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center py-20">
              <p className="text-destructive mb-2">Impossible de charger les articles.</p>
              <p className="text-muted-foreground text-sm">
                Vérifiez que le backend est lancé sur{' '}
                <code className="text-primary">{API_BASE}</code>
              </p>
            </div>
          )}

          {/* Posts grid */}
          {!loading && !error && posts.length > 0 && (
            <div className="space-y-6">
              {/* Featured post (full width) */}
              {featured && <PostCard post={featured} featured />}

              {/* 2-column grid */}
              {rest.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6">
                  {rest.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && posts.length === 0 && (
            <div className="text-center py-20">
              <Rss className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun article trouvé pour ce filtre.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-10">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-border hover:bg-muted/50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
                className="border-border hover:bg-muted/50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Bottom CTA banner */}
          <div className="mt-20 glass rounded-2xl p-8 md:p-12 text-center border border-primary/20 relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-secondary/8" />
            <div className="relative">
              <span className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-bold tracking-widest uppercase bg-primary/10 border border-primary/25 text-primary">
                Gratuit · 3 minutes
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Prêt à évaluer votre migration ?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Obtenez votre score de maturité IAM et un rapport PDF personnalisé de 7 pages.
              </p>
              <Button
                onClick={onOpenWizard}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground glow-purple group"
              >
                <Zap className="w-4 h-4 mr-2 group-hover:animate-pulse" />
                Lancer le diagnostic gratuit
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </main>

        <Footer onCTAClick={onOpenWizard} />
      </div>
    </>
  );
}
